import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trophy, Calendar, Plus, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import PropTypes from 'prop-types';
import { useUser } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils.js';
import { fetchGoals, createGoal, deleteGoal, selectAllGoals, selectGoalsLoading, selectGoalsError, updateGoal } from '../redux/slices/goalSlice.js';
import { Card, CardHeader, CardTitle, CardContent } from '../Components/ui/card.tsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../Components/ui/dialog.tsx';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '../Components/ui/sheet.tsx';
import { Input } from '../Components/ui/input.tsx';
import { Textarea } from '../Components/ui/textarea.tsx';
import { Badge } from '../Components/ui/badge.tsx';
import { Button } from '../Components/ui/button.tsx';
import { Progress } from '../Components/ui/progress.tsx';
import { Skeleton } from '../Components/ui/skeleton.tsx';
import { useIsMobile } from '../hooks/use-mobile.tsx';
import { FormattedGoalPropType } from '../types/goals.js';
import i18n from '../i18n.js';

const Goals = () => {
  const { user } = useUser();
  const dispatch = useDispatch();
  const goals = useSelector(selectAllGoals);
  const loading = useSelector(selectGoalsLoading);
  const error = useSelector(selectGoalsError);
  const { t } = useTranslation();

  const [isAddGoalOpen, setIsAddGoalOpen] = useState(false);
  const isMobile = useIsMobile();
  const [suggestedGoalsList, setSuggestedGoalsList] = useState([]);

  // New goal state
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    target: 5,
    durationMonths: 3,
  });

  // Fetch goals on component mount
  useEffect(() => {
    dispatch(fetchGoals());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  // Show error toast if there's an API error
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Example suggested goals (normally would come from API)
  useEffect(() => {
    // Mock suggested goals data

    setSuggestedGoalsList(goals.data.filter((goal) => goal.status === 'SUGGESTED'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals.data.length]);

  // Helper function to map duration_type to UI term
  const getDurationLabel = (durationType) => {
    switch (durationType) {
      case 'SHORT':
        return t('goals.durations.short');
      case 'MEDIUM':
        return t('goals.durations.medium');
      case 'LONG':
        return t('goals.durations.long');
      default:
        return t('goals.durations.unknown');
    }
  };

  // Helper function to map status to UI badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'COMPLETED':
        return 'bg-blue-100 text-blue-800';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800';
      case 'OVERDUE':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreateGoal = () => {
    // Convert to API format
    const today = new Date();
    const endDate = new Date();
    endDate.setMonth(today.getMonth() + newGoal.durationMonths);

    // Determine duration type based on months
    let durationType = 'SHORT';
    if (newGoal.durationMonths <= 1) durationType = 'SHORT';
    else if (newGoal.durationMonths <= 6) durationType = 'MEDIUM';
    else durationType = 'LONG';

    const goalData = {
      durationType,
      startDate: today.toISOString(),
      endDate: endDate.toISOString(),
      title: newGoal.title,
      description: newGoal.description || undefined,
      target: newGoal.target,
      progress: 0,
      origin: 'PATIENT',
      status: 'ACTIVE',
    };

    // Dispatch action to create goal
    dispatch(createGoal(goalData))
      .unwrap()
      .then(() => {
        // Close dialog and reset form
        setIsAddGoalOpen(false);
        setNewGoal({
          title: '',
          description: '',
          target: 5,
          durationMonths: 3,
        });
        toast.success(t('goals.notifications.created'));
      })
      .catch((err) => {
        toast.error(err?.message || t('goals.notifications.createFailed'));
      });
  };

  // Handle adopting a suggested goal
  const handleAdoptSuggestedGoal = (goal) => {
    // Create the suggested goal via API
    dispatch(
      updateGoal({
        id: goal.id,
        goal: {
          title: goal.title,
          description: goal.description,
          target: goal.target,
          durationMonths: goal.durationMonths,
          status: 'ACTIVE',
          origin: 'HANA',
          startDate: new Date(goal.startDate),
          endDate: new Date(goal.endDate),
          durationType: goal.durationType,
        },
      }),
    )
      .unwrap()
      .then(() => {
        toast.success(t('goals.notifications.adopted', { title: goal.title }));
        // Remove from suggested goals
        setSuggestedGoalsList((current) => current.filter((g) => g.id !== goal.id));
      })
      .catch((err) => {
        toast.error(err?.message || t('goals.notifications.adoptFailed'));
      });
  };

  // Handle deleting a goal
  const handleDeleteGoal = (goalId) => {
    dispatch(deleteGoal(goalId))
      .unwrap()
      .then(() => {
        toast.success(t('goals.notifications.deleted'));
      })
      .catch((err) => {
        toast.error(err?.message || t('goals.notifications.deleteFailed'));
      });
  };

  // Handle dismissing a suggested goal
  const handleDismissSuggestedGoal = (goalId) => {
    setSuggestedGoalsList((current) => current.filter((g) => g.id !== goalId));
    toast(t('goals.notifications.dismissed'));
    handleDeleteGoal(goalId);
  };

  const GoalDialog = isMobile ? Sheet : Dialog;
  const GoalDialogContent = isMobile ? SheetContent : DialogContent;
  const GoalDialogHeader = isMobile ? SheetHeader : DialogHeader;
  const GoalDialogTitle = isMobile ? SheetTitle : DialogTitle;
  const GoalDialogDescription = isMobile ? SheetDescription : DialogDescription;
  const GoalDialogFooter = isMobile ? SheetFooter : DialogFooter;

  if (loading && goals.data.length === 0) {
    return (
      <div className="container mx-auto py-8 px-4 flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <Skeleton className="w-full h-10" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="text-3xl font-bold">{t('goals.title')}</h1>
        <Button className="bg-hana-green hover:bg-hana-green/90 text-white" onClick={() => setIsAddGoalOpen(true)}>
          <Plus className="w-4 h-4 mr-1" />
          {t('goals.actions.addNew')}
        </Button>
      </div>

      {/* Suggested Goals Section */}
      {suggestedGoalsList.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Sparkles className="w-5 h-5 text-amber-500 mr-2" />
            <h2 className="text-xl font-semibold">{t('goals.suggestedByHana')}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {suggestedGoalsList.map((goal) => (
              <Card key={goal.id} className="overflow-hidden border-amber-200 bg-amber-50/40 hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-semibold text-amber-900">{goal.title}</h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                      onClick={() => handleDismissSuggestedGoal(goal.id)}
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">{t('goals.actions.dismiss')}</span>
                    </Button>
                  </div>

                  <p className="text-sm text-amber-800 mt-1 mb-3">{goal.description}</p>

                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
                      {getDurationLabel(goal.durationType)}
                    </Badge>
                    <span className="text-xs text-amber-700">
                      {t('goals.target')}: {goal.target} {t('goals.actionsLabel')}
                    </span>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full mt-4 border-amber-300 text-amber-800 hover:bg-amber-100 hover:text-amber-900"
                    onClick={() => handleAdoptSuggestedGoal(goal)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('goals.actions.addToMyGoals')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Current Goals Grid */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold mb-4">{t('goals.currentlyTracking')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {goals.data
            .filter((goal) => goal.status !== 'SUGGESTED')
            .map((goal) => {
              const progressPercentage = Math.round((goal.progress / goal.target) * 100);
              const currentStreak = goal.current_streak || 0;

              return (
                <Card key={goal.id} className="overflow-hidden hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg font-semibold line-clamp-2 leading-tight">{goal.title}</CardTitle>
                      <Badge
                        variant={goal.origin === 'HANA' ? 'secondary' : 'outline'}
                        className={cn(goal.origin === 'HANA' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' : 'border-gray-200')}
                      >
                        {goal.origin === 'HANA' ? t('goals.origin.hana') : t('goals.origin.personal')}
                      </Badge>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline" className={cn(getStatusColor(goal?.status))}>
                        {t(`goals.status.${goal?.status?.toLowerCase()}`)}
                      </Badge>
                      <Badge variant="outline" className="bg-purple-100 text-purple-800">
                        {getDurationLabel(goal?.durationType)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Simplified Streak Information */}
                    {currentStreak > 0 && (
                      <div className="mb-4 flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-amber-500" />
                        <span className="text-sm font-medium text-amber-800">{t('goals.streak', { count: currentStreak })}</span>
                      </div>
                    )}

                    {/* Overall Progress */}
                    <div className="pt-2">
                      <div className="flex justify-between items-center mb-1">
                        <div className="text-sm text-gray-600">
                          {goal?.progress} / {goal?.target}
                        </div>
                        <div
                          className={cn('text-sm font-medium', {
                            'text-red-600': progressPercentage < 25,
                            'text-amber-600': progressPercentage < 75,
                            'text-green-600': progressPercentage >= 75,
                          })}
                        >
                          {progressPercentage}%
                        </div>
                      </div>
                      <Progress value={progressPercentage} className="h-2" />
                    </div>

                    <div className="mt-4">
                      <div className="text-sm text-gray-600 mb-1 flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(goal?.startDate).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })} -{' '}
                        {new Date(goal?.endDate).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })}
                      </div>

                      {goal?.description && <p className="text-sm text-gray-700 mt-2 line-clamp-2">{goal?.description}</p>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
        </div>
      </div>

      {goals.data.length === 0 && (
        <div className="bg-gray-50 p-8 rounded-lg text-center">
          <p className="text-gray-600">{t('goals.noGoalsFound')}</p>
        </div>
      )}

      {/* Add Goal Dialog/Sheet (responsive) */}
      <GoalDialog open={isAddGoalOpen} onOpenChange={setIsAddGoalOpen}>
        <GoalDialogContent className="sm:max-w-[500px]">
          <GoalDialogHeader>
            <GoalDialogTitle>{t('goals.createNew.title')}</GoalDialogTitle>
            <GoalDialogDescription>{t('goals.createNew.description')}</GoalDialogDescription>
          </GoalDialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label htmlFor="title" className="text-sm font-medium">
                {t('goals.createNew.fields.title')}
              </label>
              <Input
                id="title"
                placeholder={t('goals.createNew.placeholders.title')}
                value={newGoal.title}
                onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium">
                {t('goals.createNew.fields.description')}
              </label>
              <Textarea
                id="description"
                placeholder={t('goals.createNew.placeholders.description')}
                value={newGoal?.description}
                onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div className="space-y-2">
                <label htmlFor="target" className="text-sm font-medium">
                  {t('goals.createNew.fields.target')}
                </label>
                <Input
                  id="target"
                  type="number"
                  min="1"
                  placeholder={t('goals.createNew.placeholders.target')}
                  value={newGoal.target}
                  onChange={(e) => setNewGoal({ ...newGoal, target: parseInt(e.target.value, 10) || 1 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="duration" className="text-sm font-medium">
                {t('goals.createNew.fields.duration')}
              </label>
              <Input
                id="duration"
                type="number"
                min="1"
                max="24"
                placeholder={t('goals.createNew.placeholders.duration')}
                value={newGoal.durationMonths}
                onChange={(e) => setNewGoal({ ...newGoal, durationMonths: parseInt(e.target.value, 10) || 1 })}
              />
            </div>
          </div>

          <GoalDialogFooter>
            <Button variant="outline" onClick={() => setIsAddGoalOpen(false)}>
              {t('goals.actions.cancel')}
            </Button>
            <Button
              loading={loading}
              onClick={handleCreateGoal}
              disabled={!newGoal.title || newGoal.target < 1}
              className="bg-hana-green hover:bg-hana-green/90"
            >
              {t('goals.actions.create')}
            </Button>
          </GoalDialogFooter>
        </GoalDialogContent>
      </GoalDialog>
    </div>
  );
};

// Use PropTypes.checkPropTypes to verify props
Goals.propTypes = {
  goals: PropTypes.arrayOf(FormattedGoalPropType),
};

export default Goals;
